import { AbstractVpaDataTableReport } from '../../report/abstract-vpa-data-table-report';
import { addDays, format, parse } from 'date-fns';
import ActiveSettingsInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/active-settings-interface';
import ColumnInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/column-interface';
import FormFieldOptionInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-option-interface';
import ServiceContainer from '../../service-container';
import SettingInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/setting-interface';
import SortByInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/sort-by-interface';
import TimesheetSearchConditionsInterface from '@/model/vo/audit/timesheet-search-conditions-interface';
import TimesheetSearchResultsVo from '@/model/vo/audit/timesheet-search-results-vo';

export default class TimesheetDataTableReport extends AbstractVpaDataTableReport
{
    public id = 'timesheets';

    public get columns(): ColumnInterface[]
    {
        const columns: ColumnInterface[] = [
            {
                key: 'task',
                title: this.i18n('general.field.task'),
            },
            {
                key: 'job.client.name',
                title: this.i18n('general.field.client'),
                width: 250,
            },
            {
                key: 'dateFrom',
                title: this.i18n('general.field.from'),
                width: 150,
            },
            {
                key: 'dateTo',
                title: this.i18n('general.field.to'),
                width: 150,
            },
            {
                key: 'chargeable',
                title: this.i18n('timesheet.field.chargeable'),
                width: 175,
            },
            {
                key: 'activity',
                title: this.i18n('timesheet.field.activity'),
                width: 150,
            },
        ];

        if (this.userId === this.currentUserId)
        {
            columns.push({
                key: 'options',
                title: this.i18n('general.field.options'),
                width: 80,
                align: 'end',
            });
        }

        return columns;
    }

    public get defaultColumns(): string[]
    {
        if (this.savedDefaultColumns)
        {
            return this.savedDefaultColumns;
        }

        return [
            'task',
            'job.client.name',
            'dateFrom',
            'dateTo',
            'chargeable',
            'activity',
            'options',
        ];
    }

    public get settings(): SettingInterface[]
    {
        return [
            {
                key: 'user',
                title: this.i18n('general.field.user').toString(),
                type: 'select',
                options: this.userOptions,
            },
            {
                key: 'date',
                title: this.i18n('general.field.date').toString(),
                type: 'date_day',
            },
        ];
    }

    public get defaultSettings(): ActiveSettingsInterface
    {
        return {
            user: this.userId,
            date: format(this.date, 'yyyy-MM-dd'),
        };
    }

    public get sortBys(): SortByInterface[]
    {
        return [
            {
                key: 'dateFrom',
                title: this.i18n('general.field.from'),
                order:
                [
                    {
                        key: 'dateFrom',
                        descending: false,
                    },
                ],
            },
        ];
    }

    public defaultSortBy = 'dateFrom';

    public results: TimesheetSearchResultsVo|null = null;
    private userId: string|null = null;
    private currentUserId: string|null = null;
    private date!: Date;
    private userOptions!: FormFieldOptionInterface[];

    public constructor(
        serviceContainer: ServiceContainer,
        userId: string|null,
        currentUserId: string|null,
        date: Date,
    )
    {
        super(serviceContainer);

        this.userId = userId;
        this.currentUserId = currentUserId;
        this.date = date;
    }

    public setUserId(userId: string|null)
    {
        this.userId = userId;
    }

    public setDate(date: Date)
    {
        this.date = date;
    }

    public async prepare(): Promise<void>
    {
        this.userOptions = await this.serviceContainer.userService.formOptionsCallback('');
    }

    public async loadResults(conditions: TimesheetSearchConditionsInterface): Promise<TimesheetSearchResultsVo>
    {
        if (conditions.date)
        {
            const date = parse(
                conditions.date, 'yyyy-MM-dd', new Date(),
            );

            conditions['dateFrom[after]'] = conditions.date;
            conditions['dateFrom[strictly_before]'] = format(addDays(date, 1), 'yyyy-MM-dd');

            delete conditions.date;
        }

        conditions.itemsPerPage = 100;

        const results = await this.serviceContainer.timesheetService.findTimesheets(conditions);

        this.results = Object.assign({}, results);

        return results;
    }
}